import { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, Shield, Users, Crown, Coins, Trophy, Swords, Send,
  Plus, LogOut, ChevronRight, Star, Lock, Medal, ChevronDown, ChevronUp
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TOKEN_KEY = "fruitCrush_token";

function api(path, opts = {}) {
  const token = localStorage.getItem(TOKEN_KEY);
  return fetch(`${API_URL}/api${path}`, {
    ...opts,
    headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json", ...opts.headers },
  }).then((r) => r.json()).catch(() => ({}));
}

// === Sub-components ===

function GuildHeader({ guild, role, onLeave, onDonate }) {
  const [donateOpen, setDonateOpen] = useState(false);
  const [donateAmt, setDonateAmt] = useState(500);

  const xpPct = guild.xp_to_next > 0 ? Math.min((guild.xp / guild.xp_to_next) * 100, 100) : 0;

  return (
    <Card className="bg-slate-800/40 border-white/5 p-4" data-testid="guild-header">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/20">
          <Shield className="w-6 h-6 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-lg font-bold text-white truncate">{guild.name}</h2>
          <div className="flex items-center gap-2 text-xs text-slate-400">
            <span>Lv.{guild.level}</span>
            <span className="text-slate-600">|</span>
            <Users className="w-3 h-3" />
            <span>{guild.member_count}/{guild.max_members}</span>
            <span className="text-slate-600">|</span>
            <Trophy className="w-3 h-3 text-amber-400" />
            <span>{guild.war_wins}W / {guild.war_losses}L</span>
          </div>
        </div>
        <button onClick={onLeave} className="p-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" data-testid="guild-leave-btn">
          <LogOut className="w-4 h-4" />
        </button>
      </div>

      {guild.description && (
        <p className="text-xs text-slate-400 mb-3">{guild.description}</p>
      )}

      {/* Guild XP bar */}
      <div className="mb-3">
        <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
          <span>Guild XP</span>
          <span>{guild.xp} / {guild.xp_to_next}</span>
        </div>
        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
          <div className="h-full bg-gradient-to-r from-amber-500 to-orange-500 rounded-full transition-all" style={{ width: `${xpPct}%` }} />
        </div>
      </div>

      {/* Treasury + Donate */}
      <div className="flex items-center gap-2">
        <div className="flex items-center gap-1.5 text-sm text-yellow-400 font-bold">
          <Coins className="w-4 h-4" />
          <span>{(guild.treasury || 0).toLocaleString()} treasury</span>
        </div>
        <button
          onClick={() => setDonateOpen(!donateOpen)}
          className="ml-auto text-xs px-3 py-1 bg-amber-500/20 text-amber-300 rounded-lg hover:bg-amber-500/30 transition-colors"
          data-testid="guild-donate-toggle"
        >
          <Plus className="w-3 h-3 inline mr-1" />Donate
        </button>
      </div>
      {donateOpen && (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="number" min={100} step={100} value={donateAmt}
            onChange={(e) => setDonateAmt(Number(e.target.value))}
            className="w-24 px-2 py-1.5 bg-slate-800 border border-slate-700 rounded-lg text-xs text-white focus:outline-none"
          />
          <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-xs h-8" onClick={() => { onDonate(donateAmt); setDonateOpen(false); }} data-testid="guild-donate-confirm">
            Donate {donateAmt}
          </Button>
        </div>
      )}
    </Card>
  );
}

function MemberList({ members, currentUserId, guildRole }) {
  const roleOrder = { leader: 0, officer: 1, member: 2 };
  const sorted = [...members].sort((a, b) => (roleOrder[a.role] || 2) - (roleOrder[b.role] || 2));
  const roleIcon = (role) => role === "leader" ? <Crown className="w-3 h-3 text-yellow-400" /> : role === "officer" ? <Medal className="w-3 h-3 text-cyan-400" /> : null;

  return (
    <Card className="bg-slate-800/40 border-white/5 p-4" data-testid="guild-members">
      <h3 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
        <Users className="w-4 h-4 text-slate-400" /> Members ({members.length})
      </h3>
      <div className="space-y-1.5 max-h-60 overflow-y-auto">
        {sorted.map((m) => (
          <div key={m.user_id} className={`flex items-center gap-2.5 p-2 rounded-lg ${m.user_id === currentUserId ? 'bg-amber-500/10 border border-amber-500/20' : 'bg-slate-800/30'}`}>
            <div className="w-8 h-8 rounded-lg bg-slate-700 flex items-center justify-center text-sm font-bold text-slate-300">
              {m.player_name?.charAt(0)?.toUpperCase() || "?"}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-white font-medium truncate">{m.player_name}</span>
                {roleIcon(m.role)}
              </div>
              <div className="text-[10px] text-slate-500">Lv.{m.highest_level} | {m.donated_coins.toLocaleString()} donated</div>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}

function GuildChat({ guildId }) {
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const chatRef = useRef(null);

  const load = useCallback(async () => {
    const data = await api(`/guilds/${guildId}/chat`);
    if (data.messages) setMessages(data.messages);
  }, [guildId]);

  useEffect(() => { load(); const iv = setInterval(load, 8000); return () => clearInterval(iv); }, [load]);
  useEffect(() => { chatRef.current?.scrollTo(0, chatRef.current.scrollHeight); }, [messages]);

  const send = async () => {
    if (!text.trim() || sending) return;
    setSending(true);
    await api(`/guilds/${guildId}/chat`, { method: "POST", body: JSON.stringify({ message: text.trim() }) });
    setText("");
    await load();
    setSending(false);
  };

  return (
    <Card className="bg-slate-800/40 border-white/5 p-4 flex flex-col" data-testid="guild-chat">
      <h3 className="font-bold text-white text-sm mb-2">Guild Chat</h3>
      <div ref={chatRef} className="flex-1 min-h-[180px] max-h-[280px] overflow-y-auto space-y-2 mb-2">
        {messages.length === 0 && <p className="text-xs text-slate-500 text-center py-8">No messages yet. Say hello!</p>}
        {messages.map((msg, i) => (
          <div key={i} className="text-xs">
            <span className={`font-bold ${msg.role === 'leader' ? 'text-yellow-400' : msg.role === 'officer' ? 'text-cyan-400' : 'text-white'}`}>
              {msg.player_name}
            </span>
            <span className="text-slate-300 ml-1.5">{msg.message}</span>
          </div>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={text} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && send()}
          placeholder="Type a message..."
          className="flex-1 px-3 py-2 bg-slate-800 border border-slate-700/50 rounded-lg text-xs text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50"
          data-testid="guild-chat-input"
        />
        <Button size="sm" onClick={send} disabled={sending} className="bg-amber-500 hover:bg-amber-600 h-8 px-3" data-testid="guild-chat-send">
          <Send className="w-3.5 h-3.5" />
        </Button>
      </div>
    </Card>
  );
}

function GuildPerks({ guild }) {
  const allPerks = Object.entries({
    1: { name: 'Coin Bonus I', desc: '+5%' },
    3: { name: 'XP Bonus I', desc: '+5%' },
    5: { name: 'Drop Rate I', desc: '+3%' },
    7: { name: 'Coin Bonus II', desc: '+10%' },
    10: { name: 'XP Bonus II', desc: '+10%' },
    12: { name: 'Extra Life', desc: '+1' },
    15: { name: 'Drop Rate II', desc: '+6%' },
    18: { name: 'Coin Bonus III', desc: '+15%' },
    20: { name: 'XP Bonus III', desc: '+15%' },
    25: { name: 'Legendary Perk', desc: '-10% craft' },
  });

  return (
    <Card className="bg-slate-800/40 border-white/5 p-4" data-testid="guild-perks">
      <h3 className="font-bold text-white text-sm mb-3 flex items-center gap-2">
        <Star className="w-4 h-4 text-amber-400" /> Guild Perks
      </h3>
      <div className="grid grid-cols-2 gap-1.5">
        {allPerks.map(([lvl, perk]) => {
          const unlocked = guild.level >= Number(lvl);
          return (
            <div key={lvl} className={`p-2 rounded-lg text-[10px] flex items-center gap-1.5 ${unlocked ? 'bg-emerald-900/20 border border-emerald-500/20' : 'bg-slate-800/30 border border-slate-700/20 opacity-50'}`}>
              {unlocked ? <Star className="w-3 h-3 text-emerald-400 shrink-0" /> : <Lock className="w-3 h-3 text-slate-600 shrink-0" />}
              <div>
                <div className={`font-bold ${unlocked ? 'text-emerald-300' : 'text-slate-500'}`}>{perk.name}</div>
                <div className="text-slate-500">Lv.{lvl} | {perk.desc}</div>
              </div>
            </div>
          );
        })}
      </div>
    </Card>
  );
}

function GuildWar({ guildId }) {
  const [war, setWar] = useState(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api(`/guilds/${guildId}/war/active`).then(d => { setWar(d.active_war); setLoaded(true); });
  }, [guildId]);

  if (!loaded) return null;
  if (!war) return (
    <Card className="bg-slate-800/40 border-white/5 p-4 text-center" data-testid="guild-war-none">
      <Swords className="w-6 h-6 text-slate-600 mx-auto mb-1.5" />
      <p className="text-xs text-slate-500">No active guild war</p>
    </Card>
  );

  const isChallenger = war.challenger_id === guildId;

  return (
    <Card className="bg-red-900/10 border-red-500/20 p-4" data-testid="guild-war-active">
      <h3 className="font-bold text-red-300 text-sm mb-3 flex items-center gap-2">
        <Swords className="w-4 h-4" /> Guild War {war.status === "completed" ? "(Finished)" : "(Active)"}
      </h3>
      <div className="flex items-center justify-between">
        <div className="text-center flex-1">
          <div className={`text-sm font-bold ${isChallenger ? 'text-amber-300' : 'text-white'}`}>{war.challenger_name}</div>
          <div className="text-2xl font-black text-white mt-1">{war.challenger_score}</div>
        </div>
        <div className="text-slate-600 text-lg font-bold px-3">VS</div>
        <div className="text-center flex-1">
          <div className={`text-sm font-bold ${!isChallenger ? 'text-amber-300' : 'text-white'}`}>{war.defender_name}</div>
          <div className="text-2xl font-black text-white mt-1">{war.defender_score}</div>
        </div>
      </div>
      {war.status === "completed" && (
        <div className="text-center mt-2 text-xs font-bold text-amber-300">
          {war.winner === "draw" ? "Draw!" : `Winner: ${war.winner === "challenger" ? war.challenger_name : war.defender_name}`}
        </div>
      )}
    </Card>
  );
}

function BrowseGuilds({ onJoin }) {
  const [guilds, setGuilds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api("/guilds/browse/list").then(d => { setGuilds(d.guilds || []); setLoading(false); });
  }, []);

  if (loading) return <p className="text-slate-500 text-sm text-center py-8">Loading guilds...</p>;

  return (
    <div className="space-y-2" data-testid="guild-browse-list">
      {guilds.length === 0 && (
        <p className="text-slate-500 text-sm text-center py-8">No guilds found. Be the first to create one!</p>
      )}
      {guilds.map((g) => (
        <Card key={g.guild_id} className="bg-slate-800/30 border-white/5 p-3 flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-amber-500/50 to-orange-600/50 flex items-center justify-center">
            <Shield className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-bold text-white truncate">{g.name}</div>
            <div className="text-[10px] text-slate-400">
              Lv.{g.level} | {g.member_count}/{g.max_members} members | {g.war_wins}W
            </div>
          </div>
          <Button
            size="sm"
            onClick={() => onJoin(g.guild_id)}
            disabled={g.member_count >= g.max_members}
            className="bg-emerald-500 hover:bg-emerald-600 text-xs h-8"
            data-testid={`join-guild-${g.guild_id}`}
          >
            Join
          </Button>
        </Card>
      ))}
    </div>
  );
}

function CreateGuildForm({ onCreate }) {
  const [name, setName] = useState("");
  const [desc, setDesc] = useState("");
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState("");

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    setError("");
    const data = await api("/guilds/create", {
      method: "POST", body: JSON.stringify({ name: name.trim(), description: desc.trim() })
    });
    if (data.success) {
      onCreate();
    } else {
      setError(data.detail || "Failed to create guild");
    }
    setCreating(false);
  };

  return (
    <Card className="bg-slate-800/40 border-white/5 p-4" data-testid="create-guild-form">
      <h3 className="font-bold text-white text-sm mb-3">Create a Guild</h3>
      <p className="text-[11px] text-slate-400 mb-3">Costs 5,000 coins. You'll become the leader.</p>
      <div className="space-y-2">
        <input
          value={name} onChange={(e) => setName(e.target.value)} placeholder="Guild name (3-24 chars)"
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700/50 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50"
          data-testid="guild-name-input"
        />
        <textarea
          value={desc} onChange={(e) => setDesc(e.target.value)} placeholder="Description (optional)"
          rows={2}
          className="w-full px-3 py-2 bg-slate-800 border border-slate-700/50 rounded-lg text-sm text-white placeholder:text-slate-600 focus:outline-none focus:border-amber-500/50 resize-none"
          data-testid="guild-desc-input"
        />
        {error && <p className="text-xs text-red-400">{error}</p>}
        <Button onClick={handleCreate} disabled={creating || name.trim().length < 3} className="w-full bg-gradient-to-r from-amber-500 to-orange-500 text-white" data-testid="guild-create-btn">
          {creating ? "Creating..." : "Create Guild (5,000 coins)"}
        </Button>
      </div>
    </Card>
  );
}

// === Main Page ===

export default function GuildPage() {
  const navigate = useNavigate();
  const [myGuild, setMyGuild] = useState(null);
  const [guildData, setGuildData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab] = useState("browse");
  const [userId, setUserId] = useState("");

  const loadGuildInfo = useCallback(async () => {
    try {
      const info = await api("/guilds/my/info");
      setMyGuild(info);
      if (info.in_guild) {
        const full = await api(`/guilds/${info.guild_id}`);
        setGuildData(full);
      }
    } catch (e) {
      /* silent */
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadGuildInfo();
    try {
      const token = localStorage.getItem(TOKEN_KEY);
      if (token) {
        const payload = JSON.parse(atob(token.split('.')[1]));
        setUserId(payload.sub || payload.id || "");
      }
    } catch {}
  }, [loadGuildInfo]);

  const handleJoin = async (guildId) => {
    const res = await api(`/guilds/${guildId}/join`, { method: "POST" });
    if (res.success) await loadGuildInfo();
  };

  const handleLeave = async () => {
    if (!guildData) return;
    if (!window.confirm("Are you sure you want to leave this guild?")) return;
    const res = await api(`/guilds/${guildData.guild_id}/leave`, { method: "POST" });
    if (res.success) { setMyGuild({ in_guild: false }); setGuildData(null); }
  };

  const handleDonate = async (amount) => {
    if (!guildData) return;
    await api(`/guilds/${guildData.guild_id}/donate`, { method: "POST", body: JSON.stringify({ amount }) });
    loadGuildInfo();
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 flex items-center justify-center">
        <Shield className="w-12 h-12 text-amber-400 animate-bounce" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-950 via-slate-900 to-slate-950 pb-16">
      <div className="container mx-auto px-3 py-4 max-w-2xl">
        {/* Header */}
        <header className="flex items-center justify-between mb-4">
          <button onClick={() => navigate(-1)} className="text-slate-300 flex items-center gap-1 text-sm hover:text-white transition-colors" data-testid="guild-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
          <h1 className="font-bold text-lg text-amber-300 flex items-center gap-1.5">
            <Shield className="w-5 h-5" /> {myGuild?.in_guild ? myGuild.guild_name : "Guilds"}
          </h1>
          <div className="w-16" />
        </header>

        {myGuild?.in_guild && guildData ? (
          /* === IN A GUILD === */
          <div className="space-y-3">
            <GuildHeader guild={guildData} role={myGuild.role} onLeave={handleLeave} onDonate={handleDonate} />
            <GuildWar guildId={guildData.guild_id} />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <MemberList members={guildData.members || []} currentUserId={userId} guildRole={myGuild.role} />
              <GuildChat guildId={guildData.guild_id} />
            </div>
            <GuildPerks guild={guildData} />
          </div>
        ) : (
          /* === NOT IN A GUILD === */
          <div className="space-y-4">
            <div className="flex gap-2 mb-2" data-testid="guild-tabs">
              {["browse", "create"].map((t) => (
                <button
                  key={t}
                  onClick={() => setTab(t)}
                  className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${
                    tab === t ? "bg-amber-500/20 text-amber-300 border border-amber-500/30" : "bg-slate-800/40 text-slate-400 border border-transparent"
                  }`}
                >
                  {t === "browse" ? "Browse Guilds" : "Create Guild"}
                </button>
              ))}
            </div>
            {tab === "browse" && <BrowseGuilds onJoin={handleJoin} />}
            {tab === "create" && <CreateGuildForm onCreate={loadGuildInfo} />}
          </div>
        )}
      </div>
    </div>
  );
}
